/**
 * Task #1328 — Hook condiviso per consumare il catalog dinamico modelli AI
 * dall'endpoint `/api/workflow-nodes/options/models`.
 *
 * Stand-alone (no react-query dep): @easyflow/ui-frontend non dichiara
 * @tanstack/react-query come dipendenza, il hook è basato su useState/useEffect
 * con polling-on-mount + refetch manuale + cache TTL in-memory cross-componenti.
 *
 * Endpoint: JWT-only (vedi `routes/workflow-node-options.routes.ts`).
 * Token estratto da localStorage (`edgvoip_tenant_token` o `edgvoip_master_token`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type ModelCapability =
  | 'chat'
  | 'embedding'
  | 'tts'
  | 'stt'
  | 'vision'
  | 'multimodal'
  | 'rerank'
  | 'realtime_voice'
  // Task #2738 — overlay virtuale: NON un valore Capability del catalog DB
  // (resta 'chat'), il route mappa 'image' su chat + filtro image-output.
  | 'image';

export type ModelProviderKey =
  | 'openai'
  | 'anthropic'
  | 'google'
  | 'claude'
  | 'openai-realtime'
  | 'gemini-voice';

export interface CatalogBadge {
  kind: 'recommended' | 'deprecated' | 'preview' | 'vision' | 'audio' | 'tool_use' | 'json' | 'streaming' | 'image';
  label: string;
  tooltip?: string;
}

export interface CatalogPricing {
  input_per_1k?: number;
  output_per_1k?: number;
  cached_input_per_1k?: number;
  currency?: string;
  unit?: string;
}

export interface ModelOption {
  value: string;
  label: string;
  badges: CatalogBadge[];
  recommended: boolean;
  deprecated: boolean;
  metadata: {
    provider: string;
    context_window: number | null;
    max_output_tokens: number | null;
    modality: string[];
    features: string[];
    pricing: CatalogPricing | null;
  };
}

export interface ModelCatalogResponse {
  options: ModelOption[];
  source: 'catalog';
  generated_at: string;
}

export interface UseModelCatalogParams {
  capability: ModelCapability;
  provider?: ModelProviderKey;
  serviceKey?: string;
  /** Skip fetch (es. per render condizionale). Default false. */
  enabled?: boolean;
}

export interface UseModelCatalogResult {
  data: ModelCatalogResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface CacheEntry {
  value: ModelCatalogResponse;
  expires: number;
}

const CACHE_TTL_MS = 30_000;
const cache = new Map<string, CacheEntry>();

function cacheKey(p: UseModelCatalogParams): string {
  return `${p.capability}|${p.provider ?? ''}|${p.serviceKey ?? ''}`;
}

function readToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return (
    localStorage.getItem('edgvoip_tenant_token') ||
    localStorage.getItem('edgvoip_master_token')
  );
}

function buildUrl(p: UseModelCatalogParams): string {
  const qs = new URLSearchParams();
  qs.set('capability', p.capability);
  if (p.provider)   qs.set('provider', p.provider);
  if (p.serviceKey) qs.set('service_key', p.serviceKey);
  return `/api/workflow-nodes/options/models?${qs.toString()}`;
}

export function useModelCatalog(params: UseModelCatalogParams): UseModelCatalogResult {
  const { enabled = true } = params;
  const [data, setData] = useState<ModelCatalogResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef<boolean>(true);

  const key = cacheKey(params);
  const url = buildUrl(params);

  const doFetch = useCallback(async (force: boolean): Promise<void> => {
    if (!force) {
      const cached = cache.get(key);
      if (cached && cached.expires > Date.now()) {
        if (mountedRef.current) {
          setData(cached.value);
          setError(null);
        }
        return;
      }
    }
    if (mountedRef.current) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const token = readToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        throw new Error(`Catalog fetch failed: HTTP ${res.status}`);
      }
      const body = (await res.json()) as ModelCatalogResponse;
      cache.set(key, { value: body, expires: Date.now() + CACHE_TTL_MS });
      if (mountedRef.current) {
        setData(body);
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      if (mountedRef.current) setError(e);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [key, url]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      void doFetch(false);
    }
    return () => {
      mountedRef.current = false;
    };
  }, [enabled, doFetch]);

  const refetch = useCallback(async (): Promise<void> => {
    await doFetch(true);
  }, [doFetch]);

  return {
    data,
    isLoading,
    isError: error !== null,
    error,
    refetch,
  };
}

/** Invalida manualmente la cache in-memory (utile post mutate admin). */
export function invalidateModelCatalogCache(): void {
  cache.clear();
}
